import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './context/AuthContext.jsx';
import LoadingState from './components/LoadingState.jsx';
import LandingPage from './pages/LandingPage.jsx';
import LoginPage from './pages/LoginPage.jsx';
import RegisterPage from './pages/RegisterPage.jsx';
import ForgotPasswordPage from './pages/ForgotPasswordPage.jsx';
import ResetPasswordPage from './pages/ResetPasswordPage.jsx';
import GuestPage from './pages/GuestPage.jsx';
import RequestFormPage from './pages/RequestFormPage.jsx';
import AccountSettingsPage from './pages/AccountSettingsPage.jsx';
import AccountRequestRedirect from './pages/AccountRequestRedirect.jsx';
import TrackRequestPage from './pages/TrackRequestPage.jsx';
import NotFoundPage from './pages/NotFoundPage.jsx';

// ProductDetailPage, CatalogBrowsePage, and ProformaPage are the lazy-loaded
// routes in this app — every other screen stays eagerly bundled together
// (this file's own comment below, unchanged for them). This exists ONLY to
// keep the portal's hard 120KB-gzipped-JS budget (root CLAUDE.md, "enforced,
// not aspirational") intact: the main bundle was already measured at
// 119.38KB before the product-detail-page task, 0.62KB of headroom
// (PROGRESS.md's own running log of this exact number). Shipping a whole
// new detail screen eagerly pushed it to ~120.95KB; splitting that one
// route back out of the main chunk (ProductDetailPage, below) is the
// standard, dependency-free React fix (no new package — React.lazy/Suspense
// are built in) rather than cutting scope or raising the budget — but on
// its own that still left the bundle at ~120.92KB, still over cap.
// CatalogBrowsePage (the next-largest single-route file in the whole app,
// reached only via /browse and /catalog, never needed for the landing
// page's own first paint) and ProformaPage (reached only via a
// notification/tracking link at /proforma/:token, never part of the
// guest-submission critical path portal/CLAUDE.md calls out as the thing
// speed matters most for) split out the same way, measured via
// dist/stats.html's treemap for each real contribution. No other route is
// touched — in particular RequestFormPage, GuestPage, and the login/account
// routes stay eager: they sit ON the submission path this app exists to
// keep fast, where a chunk-fetch stall is the wrong trade even to save a
// few KB.
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage.jsx'));
const CatalogBrowsePage = lazy(() => import('./pages/CatalogBrowsePage.jsx'));
const ProformaPage = lazy(() => import('./pages/ProformaPage.jsx'));

// Only reachable with a live 'customer'-role session. Anything else
// (no session, or a session for the wrong role — see
// context/AuthContext.jsx's header comment) redirects to /login, where
// LoginPage's own wrong-role handling takes over — this guard only decides
// what to RENDER, it is never treated as a security boundary (every real
// API call still enforces requireAuth/requireRole('customer') server-side
// regardless of what this component does).
function RequireCustomer({ children }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingState />;
  }
  if (!user || user.role !== 'customer') {
    return <Navigate to="/login" replace />;
  }
  return children;
}

export default function App() {
  const { user, isLoading } = useAuth();
  const isCustomer = Boolean(user) && user.role === 'customer';

  // No route-level code splitting (portal/CLAUDE.md) — every screen below
  // is imported eagerly above and ships together in one bundle, EXCEPT
  // ProductDetailPage, CatalogBrowsePage, and ProformaPage (see their
  // lazy() imports above and their Routes below) — a budget-driven set of
  // exceptions, not a reversal of this app's general policy.
  return (
    <Routes>
      <Route
        path="/"
        element={
          isLoading ? (
            <LoadingState />
          ) : isCustomer ? (
            <Navigate to="/request" replace />
          ) : (
            <LandingPage />
          )
        }
      />
      <Route
        path="/login"
        element={isCustomer ? <Navigate to="/request" replace /> : <LoginPage />}
      />
      {/* Customer-accounts task: registration and password reset, both
          genuinely public (a logged-in customer has no reason to be here,
          same "bounce to /request" treatment as /login above). */}
      <Route
        path="/register"
        element={isCustomer ? <Navigate to="/request" replace /> : <RegisterPage />}
      />
      <Route
        path="/forgot-password"
        element={isCustomer ? <Navigate to="/request" replace /> : <ForgotPasswordPage />}
      />
      {/* No isCustomer redirect here — a logged-in customer resetting their
          password (e.g. a shared/shop terminal) is a real, legitimate case
          this route must not block. */}
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      {/* NOT "/guest" — that literal path collides with the dev proxy's
          "/guest" API prefix (vite.config.js's API_PATH_PREFIXES; the
          backend's own guest OTP/submission routes are ALSO mounted at
          "/guest" — see backend/src/app.js). A page navigation to
          "/guest" gets proxied straight to the backend instead of ever
          reaching this SPA route, a real bug caught only by actually
          driving this route end to end, not by reading the code back.

          A logged-in customer is bounced to /request — same treatment as
          /login and /register above. The guest flow's whole point is
          proving an anonymous submitter's email is real via OTP; an
          account holder's session already proves that (it was verified at
          registration), so routing them through /start would just be
          pointless re-verification on every request. The SiteHeader's
          "Send a request" link already points them at /request directly;
          this is the safety net for a typed URL or a stale link. */}
      <Route
        path="/start"
        element={isCustomer ? <Navigate to="/request" replace /> : <GuestPage />}
      />
      {/* Genuinely public — a guest browsing before verifying an email is
          exactly who this screen is for, not just an already-authenticated
          buyer. Not "/public" or "/catalog": both are backend API path
          prefixes (vite.config.js's API_PATH_PREFIXES /
          backend/src/app.js's /catalog and /public/catalog mounts) — same
          collision class as "/guest" above. Lazy (see the import above,
          next to ProductDetailPage's identical reasoning) — same
          LoadingState-as-Suspense-fallback trick, so there is no visible
          seam between "chunk downloading" and "this page's own query
          loading" skeleton. */}
      <Route
        path="/browse"
        element={
          <Suspense fallback={<LoadingState />}>
            <CatalogBrowsePage />
          </Suspense>
        }
      />
      {/* /catalog: the SAME CatalogBrowsePage component, mounted at a
          second path so the hero search box and the featured grid's "View
          full catalog" link (pages/LandingPage.jsx) can point at a literal
          URL — /catalog?q=... and a bare /catalog both land here. NOT in
          API_PATH_PREFIXES (portal/vite.config.js's own header comment
          explains why: that exact prefix is the SALES-authenticated router
          on the backend) — the dev proxy never intercepts this path, so it
          reaches the SPA router same as /browse does. /browse itself is
          untouched: the category tiles above still link there. */}
      <Route
        path="/catalog"
        element={
          <Suspense fallback={<LoadingState />}>
            <CatalogBrowsePage />
          </Suspense>
        }
      />
      {/* One product's full detail — reached by clicking a featured card
          (ProductCard.jsx's `variant="featured"`) or any future catalog
          link. Genuinely public, same as /browse / /catalog above. Lazy
          (see the import above) — LoadingState doubles as the Suspense
          fallback, the same skeleton this component itself already shows
          while its own data queries are in flight, so there is no visible
          seam between "chunk downloading" and "data loading". */}
      <Route
        path="/catalog/:id"
        element={
          <Suspense fallback={<LoadingState />}>
            <ProductDetailPage />
          </Suspense>
        }
      />
      {/* /track is where a buyer checks on a request. A logged-in customer
          sees their own requests + proformas listed (each row links to
          /proforma/:token); a guest gets the reference-number + email
          lookup, which lands on the same /proforma/:token page. NOTE: no
          "/track/..." child SPA route — vite.config.js proxies "^/track/.+"
          straight to the backend API, so a client route there would never
          render. See pages/TrackRequestPage.jsx. */}
      <Route path="/track" element={<TrackRequestPage />} />
      {/* The buyer's in-page proforma review + notes. The SPA route is
          "/proforma/:token", NOT "/track/:token" — the dev proxy (and Nginx)
          forwards everything under "/track/..." straight to the backend API,
          so a client route there would never render. Every notification and
          PDF "track this request" link points here
          (services/trackingService.js#buildTrackingUrl). Genuinely public:
          the token is the only credential. Lazy (see the import above) —
          same LoadingState-as-Suspense-fallback trick as the other two
          lazy routes. */}
      <Route
        path="/proforma/:token"
        element={
          <Suspense fallback={<LoadingState />}>
            <ProformaPage />
          </Suspense>
        }
      />
      <Route
        path="/request"
        element={
          <RequireCustomer>
            <RequestFormPage />
          </RequireCustomer>
        }
      />
      {/* /account is now ONLY company details, contact info, and
          notification preferences — the request list + detail moved to
          /track. The two redirects below keep old /account request links
          working. */}
      <Route
        path="/account"
        element={
          <RequireCustomer>
            <AccountSettingsPage />
          </RequireCustomer>
        }
      />
      <Route path="/account/settings" element={<Navigate to="/account" replace />} />
      <Route path="/account/requests" element={<Navigate to="/track" replace />} />
      <Route
        path="/account/requests/:id"
        element={
          <RequireCustomer>
            <AccountRequestRedirect />
          </RequireCustomer>
        }
      />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
